import { addDays, addMonths, format } from 'date-fns';

import {
  CASHBACK_LABEL,
  CASHBACK_PERCENT,
  DEFAULT_PAYCHECK_COUNT,
  FIRST_PAY_DAY,
  FIRST_PAY_MONTH_INDEX,
  FIRST_PAY_YEAR,
  MAX_PAYCHECKS,
  PAYCHECK_MAX_CENTS,
  PAYCHECK_MIN_CENTS,
  PAYCHECK_STEP_CENTS,
  SAMPLE_PURCHASE,
  SAMPLE_PURCHASE_CENTS,
  SPENDABLE_PERCENT,
  SAVINGS_APY_LABEL,
  SAVINGS_APY_PERCENT,
} from 'prototypes/securedCard/prototypeAssumptions';
import {
  LedgerEntry,
  LedgerKind,
  PayCadence,
  PaycheckRow,
  StoryInput,
  StoryMilestone,
  StoryProjection,
  StoryState,
} from 'prototypes/securedCard/types';

const MILESTONE_ORDER: StoryMilestone[] = [
  StoryMilestone.NotStarted,
  StoryMilestone.Started,
  StoryMilestone.PaycheckSet,
  StoryMilestone.GrowthSeen,
  StoryMilestone.PurchasePaid,
  StoryMilestone.Complete,
];

const PERIODS_PER_YEAR: Record<PayCadence, number> = {
  [PayCadence.Weekly]: 52,
  [PayCadence.Biweekly]: 26,
  [PayCadence.Monthly]: 12,
};

const FIRST_PAY_DATE = new Date(FIRST_PAY_YEAR, FIRST_PAY_MONTH_INDEX, FIRST_PAY_DAY);

export const milestoneRank = (milestone: StoryMilestone): number => {
  const index = MILESTONE_ORDER.indexOf(milestone);
  if (index < 0) {
    throw new Error(`Unknown story milestone: ${milestone}`);
  }
  return index;
};

export const cadencePhrase = (cadence: PayCadence): string => {
  switch (cadence) {
    case PayCadence.Weekly:
      return 'every week';
    case PayCadence.Biweekly:
      return 'every two weeks';
    case PayCadence.Monthly:
      return 'every month';
    default:
      throw new Error(`Unknown pay cadence: ${cadence}`);
  }
};

export const cadenceLabel = (cadence: PayCadence): string => {
  switch (cadence) {
    case PayCadence.Weekly:
      return 'Every week';
    case PayCadence.Biweekly:
      return 'Every two weeks';
    case PayCadence.Monthly:
      return 'Every month';
    default:
      throw new Error(`Unknown pay cadence: ${cadence}`);
  }
};

export const clampPaycheckCents = (cents: number): number => {
  const stepped = Math.round(cents / PAYCHECK_STEP_CENTS) * PAYCHECK_STEP_CENTS;
  return Math.min(PAYCHECK_MAX_CENTS, Math.max(PAYCHECK_MIN_CENTS, stepped));
};

export const clampPaycheckCount = (count: number): number => Math.min(MAX_PAYCHECKS, Math.max(1, Math.round(count)));

const spendableShare = (savingsCents: number): { availableCents: number; heldCents: number } => {
  const safeSavings = Math.max(0, Math.round(savingsCents));
  const availableCents = Math.floor((safeSavings * SPENDABLE_PERCENT) / 100);
  return { availableCents, heldCents: safeSavings - availableCents };
};

export const periodInterestCents = (savingsCents: number, cadence: PayCadence): number =>
  Math.round((savingsCents * SAVINGS_APY_PERCENT) / 100 / PERIODS_PER_YEAR[cadence]);

export const cashbackOnSpendCents = (spendCents: number): number => Math.round((spendCents * CASHBACK_PERCENT) / 100);

const payDate = (cadence: PayCadence, index: number): Date => {
  const step = index - 1;
  switch (cadence) {
    case PayCadence.Weekly:
      return addDays(FIRST_PAY_DATE, step * 7);
    case PayCadence.Biweekly:
      return addDays(FIRST_PAY_DATE, step * 14);
    case PayCadence.Monthly:
      return addMonths(FIRST_PAY_DATE, step);
    default:
      throw new Error(`Unknown pay cadence: ${cadence}`);
  }
};

const formatPayDate = (date: Date): string => format(date, 'MMM d, yyyy');

const buildPaychecks = (perPaycheckCents: number, cadence: PayCadence, paycheckCount: number): PaycheckRow[] => {
  const rows: PaycheckRow[] = [];
  let savingsCents = 0;
  let interestToDateCents = 0;

  for (let index = 1; index <= paycheckCount; index += 1) {
    savingsCents += perPaycheckCents;
    const interestThisPeriodCents = periodInterestCents(savingsCents, cadence);
    interestToDateCents += interestThisPeriodCents;
    const share = spendableShare(savingsCents);
    rows.push({
      index,
      dateLabel: formatPayDate(payDate(cadence, index)),
      depositCents: perPaycheckCents,
      savingsCents,
      availableCents: share.availableCents,
      heldCents: share.heldCents,
      interestThisPeriodCents,
      interestToDateCents,
    });
  }

  return rows;
};

export const projectStory = (input: StoryInput): StoryProjection => {
  const perPaycheckCents = clampPaycheckCents(input.perPaycheckCents);
  const paycheckCount = clampPaycheckCount(input.paycheckCount || DEFAULT_PAYCHECK_COUNT);
  const { cadence } = input;
  const paychecks = buildPaychecks(perPaycheckCents, cadence, paycheckCount);
  const latest = paychecks[paychecks.length - 1];
  const requestedPurchaseCents = SAMPLE_PURCHASE_CENTS;
  const purchaseCents = Math.min(requestedPurchaseCents, latest.availableCents);
  const afterPayment = spendableShare(latest.savingsCents - purchaseCents);
  const nextSavingsCents = latest.savingsCents - purchaseCents + perPaycheckCents;
  const nextShare = spendableShare(nextSavingsCents);

  return {
    perPaycheckCents,
    cadence,
    paycheckCount,
    paychecks,
    savingsCents: latest.savingsCents,
    availableCents: latest.availableCents,
    heldCents: latest.heldCents,
    interestCents: latest.interestToDateCents,
    requestedPurchaseCents,
    purchaseCents,
    purchaseWasReduced: purchaseCents < requestedPurchaseCents,
    cashbackCents: cashbackOnSpendCents(purchaseCents),
    paymentDateLabel: formatPayDate(addDays(payDate(cadence, paycheckCount), 1)),
    savingsAfterPaymentCents: latest.savingsCents - purchaseCents,
    availableAfterPaymentCents: afterPayment.availableCents,
    heldAfterPaymentCents: afterPayment.heldCents,
    nextSavingsCents,
    nextAvailableCents: nextShare.availableCents,
    nextPaycheckAvailableGainCents: nextShare.availableCents - afterPayment.availableCents,
  };
};

const hasReached = (milestone: StoryMilestone, target: StoryMilestone): boolean =>
  milestoneRank(milestone) >= milestoneRank(target);

export const buildLedger = (state: StoryState, projection: StoryProjection): LedgerEntry[] => {
  if (!hasReached(state.milestone, StoryMilestone.PaycheckSet)) {
    return [];
  }

  const entries: LedgerEntry[] = projection.paychecks.map((paycheck) => ({
    id: `payroll-${paycheck.index}`,
    kind: LedgerKind.Payroll,
    title: 'Payroll deduction',
    detail: `${cadenceLabel(projection.cadence)} into savings. ${SPENDABLE_PERCENT}% becomes spending power.`,
    dateLabel: paycheck.dateLabel,
    amountCents: paycheck.depositCents,
    affectsSavings: true,
  }));

  entries.push({
    id: 'interest',
    kind: LedgerKind.Interest,
    title: 'Interest on savings',
    detail: `${SAVINGS_APY_LABEL} on savings across these paydays.`,
    dateLabel: projection.paychecks[projection.paychecks.length - 1].dateLabel,
    amountCents: projection.interestCents,
    affectsSavings: false,
  });

  if (!hasReached(state.milestone, StoryMilestone.PurchasePaid) || projection.purchaseCents === 0) {
    return entries;
  }

  entries.push({
    id: 'payment',
    kind: LedgerKind.Payment,
    title: `${SAMPLE_PURCHASE.merchant}, paid from savings`,
    detail: 'Paid automatically from this account. On time, with nothing left to revolve.',
    dateLabel: projection.paymentDateLabel,
    amountCents: -projection.purchaseCents,
    affectsSavings: true,
  });

  entries.push({
    id: 'cashback',
    kind: LedgerKind.Cashback,
    title: 'Cashback',
    detail: `${CASHBACK_LABEL} on this purchase. A typical flat rate. Tracked as rewards.`,
    dateLabel: projection.paymentDateLabel,
    amountCents: projection.cashbackCents,
    affectsSavings: false,
  });

  return entries;
};
